import { useEffect, useMemo, useState, useRef } from "react";
import _ from "lodash";
import { Box, Grid2, Link, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { returnIsValid, useConstants } from "../../Main/useConstants";
import { ModalContainer } from "../../components/ModalContainer"
import { InvestigationButton } from "../../components/InvestigationButton";
import { useSelector } from "react-redux";
import { CreateResourceFromAnywhere } from "../../Resources/Modals/CreateResourceFromAnywhere";

const validateForm = (form) => {
  const { type, resource } = form;
  const errors = {};
  if (!type) {
    errors.type = true;
  }
  if (!resource) {
    errors.resource = true;
  }
  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
}

const schemaResource = {
  type: "",
  resource: "",
  details: "",
}

export const AddResourceForActivity = ({
  show,
  onHide,
  resources,
  setResources,
  selectedResource,
  setSelectedResource,
  investigation,
  setInvestigation,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const { resources: { resourceTypeOpts, accessResourcesOpts } } = useConstants();

  const { accessResourcesInfo, financialResourcesInfo, materialResourcesInfo } = useSelector((state) => state.jaguar.currentReport.investigation);

  const [type, setType] = useState(selectedResource !== null ? resources[selectedResource].type : "");
  const [resource, setResource] = useState(selectedResource !== null ? resources[selectedResource].resource : "");
  const [details, setDetails] = useState(selectedResource !== null ? resources[selectedResource].details : "");

  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);

  const [activeDetails, setActiveDetails] = useState(false);
  const previousTypeRef = useRef("");

  const [showCreateResourceFromAnywhere, setShowCreateResourceFromAnywhere] = useState(false);

  const optionsByType = useMemo(() => {
    if (type === "access") return accessResourcesInfo.approvedFormatted.map((resource) => ({
      label: accessResourcesOpts.find((opt) => opt.value === resource.label)?.label || resource.label,
      value: resource.value,
    }));
    if (type === "financial") return financialResourcesInfo.approvedFormatted;
    if (type === "material") return materialResourcesInfo.approvedFormatted;
    return [];
  }, [type, accessResourcesInfo, financialResourcesInfo, materialResourcesInfo]);

  const cleanedResourceTypeOpts = useMemo(() => {
    return resourceTypeOpts.filter(opt => {
      if (opt.value === "human") return false;
      if (opt.value === "access") return accessResourcesInfo.approvedFormatted.length > 0;
      if (opt.value === "financial") return financialResourcesInfo.approvedFormatted.length > 0;
      if (opt.value === "material") return materialResourcesInfo.approvedFormatted.length > 0;
    });
  }, [resourceTypeOpts]);

  useEffect(() => {
    if (selectedResource !== null) {
      setType(resources[selectedResource].type);
      setResource(resources[selectedResource].resource);
      setDetails(resources[selectedResource].details);
      setActiveDetails(resources[selectedResource].details !== "");
    } else {
      setType("");
      setResource("");
      setDetails("");
    }
  }, [selectedResource, resources]);

  // Validate on every change
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(resource);
      setErrors(errors);
    }
  }, [resource]);

  // Reset resource when type changes
  useEffect(() => {
    if (selectedResource === null && resource && previousTypeRef.current && type !== previousTypeRef.current) {
      setResource("");
    }
    previousTypeRef.current = type;
  }, [type, selectedResource, resource]);

  // Reset values when modal is closed
  useEffect(() => {
    if (!show) {
      resetValues();
    }
  }, [show]);

  const handleSubmit = () => {
    const [isValid, errors] = validateForm({ type, resource, details });
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("resources.missing_required_inputs") });
      return;
    };

    const newResource = { type, resource, details };

    // selected resource
    if (selectedResource !== null) {
      const newResources = [...resources];
      newResources[selectedResource] = newResource;
      setResources(newResources);
    } else { 
      const newResources = [...resources];
      newResources.push(newResource);
      setResources(newResources);
    }
    onHide();
  }

  const resetValues = () => {
    setType("");
    setResource("");
    setDetails("");
    setTriedToSave(false);
    setErrors({});
    setActiveDetails(false);
    setSelectedResource(null);
  }

  return (
    <>
      <CreateResourceFromAnywhere
        show={showCreateResourceFromAnywhere}
        onHide={() => setShowCreateResourceFromAnywhere(false)}
        investigation={investigation}
        setInvestigation={setInvestigation}
      />
      <ModalContainer show={show} onHide={onHide}>
        <Typography fontWeight={700} fontSize={28} color={theme.palette.ethicsBlue.main}>
          {t("activityPlan.assign_resource")}
        </Typography>
        <Typography fontSize={16} color="oxfordBlue" >
          {t("activityPlan.assign_resource_description")}
        </Typography>

        <div className="hr-div"></div>

        <Grid2 container spacing={2}>
          
          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("activityPlan.resource_type")}
              name="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={cleanedResourceTypeOpts}
              required={true}
              isValid={returnIsValid(errors?.type, triedToSave) ?? null}
              showSuccessValidation
              showHelpText={false}
            />
          </Grid2>

          {
            !!type && (
              <Grid2 size={{ xs: 12, md: 6 }}>
                <EGBasicSelect
                  label={type === "access" 
                    ? t("activityPlan.access_resource") 
                    : type === "financial" 
                      ? t("activityPlan.financial_resource") 
                      : type === "material" 
                        ? t("activityPlan.material_resource")
                        : t("")}
                  name="resource"
                  value={resource}
                  onChange={(e) => setResource(e.target.value)}
                  options={optionsByType}
                  required={true}
                  isValid={returnIsValid(errors?.resource, triedToSave) ?? null}
                  showSuccessValidation
                  showHelpText={false}
                />
                {
                  !activeDetails && (
                    <Box display="flex" justifyContent="flex-end">
                      <Link
                        onClick={() => setActiveDetails(true)}
                        underline="hover"
                        mt={.5}
                      >
                        + {t("activityPlan.add_details")}
                      </Link>
                    </Box>
                  ) 
                }
              </Grid2>
            )
          }


          <Grid2 size={12} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            {
              activeDetails && (
                <EGInput
                  label={t("activityPlan.details")}
                  name="details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  type="textarea"
                  multiline
                  rows={3}
                />
              )
            }
          </Grid2>

        </Grid2>

        {/* Buttons */}
        <Stack 
          direction={{ xs: "column", sm: "row" }}
          spacing={2} 
          justifyContent="flex-end"
          mt={2}
        >
          <InvestigationButton
            label={t("activityPlan.cancel")}
            icon="false"
            size="medium"
            variant="main"
            outlined
            onClick={onHide}
          />
          <InvestigationButton
            label={t("activityPlan.create_resource")}
            icon="false"
            size="medium"
            variant="main"
            onClick={() => setShowCreateResourceFromAnywhere(true)}
          />
          <InvestigationButton
            label={t("activityPlan.save")}
            icon="false"
            size="medium"
            variant="main"
            onClick={handleSubmit}
          />
        </Stack>

      </ModalContainer>
    </>
  )
}